import React, { useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Button,
    Card,
    Col,
    Empty,
    Form,
    AutoComplete,
    Input,
    InputNumber,
    Modal,
    Row,
    Select,
    Space,
    Statistic,
    Table,
    Tag,
    Typography,
    message
} from 'antd';
import {
    Bar,
    BarChart,
    CartesianGrid,
    Cell,
    Legend,
    Pie,
    PieChart,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis
} from 'recharts';
import { DollarOutlined, EditOutlined, EyeOutlined, PlusOutlined, ReloadOutlined, StopOutlined } from '@ant-design/icons';
import { BlobFileInfo, CompanyUser, Expense, ExpenseCategory, ExpenseOptions, FinancialMetrics, OrganizationSearch, SalesDocumentFileRequest } from '../store/user/types';
import { formatCalendarDate, getOrganizationToday } from '../services/dateTime';

const { Text } = Typography;

const DEFAULT_ORGANIZATION_ID = '9d779469-6321-4978-8898-7fa114e134fe';
const DEFAULT_ORGANIZATION_NAME = 'CODING THEBLUE';
const chartColors = ['#2177f5', '#ef4444', '#19b37b', '#f5a623', '#7c3aed', '#0ea5e9', '#f97316', '#64748b'];

type ExpenseStatusFilter = 'TODOS' | 'PENDIENTE' | 'PAGADO' | 'ANULADO';
type PeriodGroup = 'day' | 'month' | 'quarter' | 'year';

interface PlanBillingExpensesProps {
    getExpenseCategories: (onlyActive?: boolean) => Promise<ExpenseCategory[]>;
    postExpenseCategory: (value: Object) => Promise<ExpenseCategory>;
    putExpenseCategory: (value: Object) => Promise<ExpenseCategory>;
    getExpenses: (params?: { from?: string; to?: string; categoryId?: string; organizationId?: string; status?: string }) => Promise<Expense[]>;
    postExpense: (value: Object) => Promise<Expense>;
    putExpense: (value: Object) => Promise<Expense>;
    cancelExpense: (id: string) => Promise<Expense>;
    getExpenseDocument: (id: string) => Promise<BlobFileInfo>;
    getFinancialMetrics: (params?: { from?: string; to?: string; groupBy?: string; organizationId?: string }) => Promise<FinancialMetrics>;
    getExpenseOptions: (organizationId?: string) => Promise<ExpenseOptions>;
    getCompanyUsers: (organizationId?: string) => Promise<CompanyUser[]>;
    searchOrganizations: (value: string, take?: number) => Promise<OrganizationSearch[]>;
}

const money = (value?: number | null, currency = 'USD') =>
    new Intl.NumberFormat('es-EC', {
        style: 'currency',
        currency,
        minimumFractionDigits: 2
    }).format(Number(value || 0));

const toIsoDate = (value?: string | null) => value ? `${value}T00:00:00Z` : null;
const toDateInput = (value?: string | null) => value ? value.slice(0, 10) : '';

const formatDate = (value?: string | null) => formatCalendarDate(value) || 'Sin fecha';

const getStatusColor = (status?: string | null) => {
    if (status === 'PAGADO') return 'green';
    if (status === 'PENDIENTE') return 'gold';
    if (status === 'ANULADO') return 'red';
    return 'default';
};

const shortLabel = (value: string, length = 16) => value.length > length ? `${value.slice(0, length - 1)}.` : value;

const fileToBase64 = (file: File): Promise<SalesDocumentFileRequest> => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
        const result = String(reader.result || '');
        const base64File = result.includes(',') ? result.split(',')[1] : result;
        resolve({
            base64File,
            fileName: file.name
        });
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
});

interface ReimbursementDebtGroup {
    key: string;
    userName: string;
    amount: number;
    paidAmount: number;
    pendingAmount: number;
    count: number;
    currency: string;
    expenses: Expense[];
}

const PlanBillingExpenses: React.FC<PlanBillingExpensesProps> = ({
    getExpenseCategories,
    postExpenseCategory,
    putExpenseCategory,
    getExpenses,
    postExpense,
    putExpense,
    cancelExpense,
    getExpenseDocument,
    getFinancialMetrics,
    getExpenseOptions,
    getCompanyUsers,
    searchOrganizations
}) => {
    const [expenseForm] = Form.useForm();
    const [categoryForm] = Form.useForm();
    const [reimbursementForm] = Form.useForm();
    const [categories, setCategories] = useState<ExpenseCategory[]>([]);
    const [expenses, setExpenses] = useState<Expense[]>([]);
    const [metrics, setMetrics] = useState<FinancialMetrics | null>(null);
    const [expenseOptions, setExpenseOptions] = useState<ExpenseOptions>({ providers: [], paymentMethods: [] });
    const [companyUsers, setCompanyUsers] = useState<CompanyUser[]>([]);
    const [organizations, setOrganizations] = useState<OrganizationSearch[]>([]);
    const [expenseModalOpen, setExpenseModalOpen] = useState(false);
    const [categoryModalOpen, setCategoryModalOpen] = useState(false);
    const [debtModalOpen, setDebtModalOpen] = useState(false);
    const [debtLoading, setDebtLoading] = useState(false);
    const [debtExpenses, setDebtExpenses] = useState<Expense[]>([]);
    const [debtPersonFilter, setDebtPersonFilter] = useState('');
    const [reimbursementModalOpen, setReimbursementModalOpen] = useState(false);
    const [selectedReimbursementExpense, setSelectedReimbursementExpense] = useState<Expense | null>(null);
    const [expenseDocumentFile, setExpenseDocumentFile] = useState<SalesDocumentFileRequest | null>(null);
    const [expenseDocumentLoading, setExpenseDocumentLoading] = useState(false);
    const [documentPreview, setDocumentPreview] = useState<{ title: string; url: string } | null>(null);
    const [selectedExpense, setSelectedExpense] = useState<Expense | null>(null);
    const [filters, setFilters] = useState<{
        from?: string;
        to?: string;
        categoryId?: string;
        organizationId?: string;
        search?: string;
        providerName?: string;
        paymentSource?: string;
        userId?: string;
        status: ExpenseStatusFilter;
        groupBy: PeriodGroup;
    }>({
        status: 'TODOS',
        groupBy: 'month'
    });

    const categoryOptions = useMemo(() => categories.map(category => ({
        value: category.id || '',
        label: category.name
    })).filter(option => option.value), [categories]);

    const organizationOptions = useMemo(() => {
        const map = new Map<string, string>();
        map.set(DEFAULT_ORGANIZATION_ID, DEFAULT_ORGANIZATION_NAME);
        organizations.forEach(organization => {
            map.set(organization.id, `${organization.name} - ${organization.tenant}`);
        });
        expenses.forEach(expense => {
            if (expense.organizationId) {
                map.set(expense.organizationId, expense.organizationName || expense.organizationId);
            }
        });

        return Array.from(map.entries()).map(([value, label]) => ({ value, label }));
    }, [expenses, organizations]);

    const providerOptions = useMemo(() => {
        const values = new Set<string>();
        expenseOptions.providers.forEach(value => values.add(value));
        expenses.forEach(expense => {
            if (expense.providerName) {
                values.add(expense.providerName);
            }
        });
        return Array.from(values).sort().map(value => ({ value }));
    }, [expenseOptions.providers, expenses]);

    const paymentMethodOptions = useMemo(() => {
        const values = new Set<string>();
        expenseOptions.paymentMethods.forEach(value => values.add(value));
        expenses.forEach(expense => {
            if (expense.paymentMethod) {
                values.add(expense.paymentMethod);
            }
        });
        return Array.from(values).sort().map(value => ({ value }));
    }, [expenseOptions.paymentMethods, expenses]);

    const companyUserOptions = useMemo(() => companyUsers.map(user => ({
        value: user.id || '',
        label: `${user.fullName} - ${user.type}${user.category ? ` / ${user.category}` : ''}`
    })).filter(option => option.value), [companyUsers]);

    const filteredExpenses = useMemo(() => {
        const search = (filters.search || '').trim().toLowerCase();

        return expenses.filter(expense => {
            if (filters.providerName && expense.providerName !== filters.providerName) {
                return false;
            }

            if (filters.paymentSource && (expense.paymentSource || 'EMPRESA') !== filters.paymentSource) {
                return false;
            }

            if (filters.userId) {
                const reimbursementUserId = expense.reimbursement?.userId;
                if (expense.userId !== filters.userId && reimbursementUserId !== filters.userId) {
                    return false;
                }
            }

            if (!search) {
                return true;
            }

            const searchableText = [
                expense.providerName,
                expense.description,
                expense.number,
                expense.expenseCategoryName,
                expense.organizationName,
                expense.userName,
                expense.paymentSource,
                expense.reimbursement?.userName
            ]
                .filter(Boolean)
                .join(' ')
                .toLowerCase();

            return searchableText.includes(search);
        });
    }, [expenses, filters.paymentSource, filters.providerName, filters.search, filters.userId]);

    const reimbursementDebtBreakdown = useMemo<ReimbursementDebtGroup[]>(() => {
        const map = new Map<string, ReimbursementDebtGroup>();

        debtExpenses.forEach(expense => {
            const reimbursement = expense.reimbursement;
            const pendingAmount = Number(reimbursement?.pendingAmount ?? ((reimbursement?.amount || 0) - (reimbursement?.paidAmount || 0)));

            if (!reimbursement || expense.status === 'ANULADO' || reimbursement.status === 'ANULADO' || pendingAmount <= 0) {
                return;
            }

            const key = reimbursement.userId || expense.userId || 'sin-persona';
            const current = map.get(key) || {
                key,
                userName: reimbursement.userName || expense.userName || 'Sin persona',
                amount: 0,
                paidAmount: 0,
                pendingAmount: 0,
                count: 0,
                currency: expense.currency || 'USD',
                expenses: []
            };

            current.amount += Number(reimbursement.amount || 0);
            current.paidAmount += Number(reimbursement.paidAmount || 0);
            current.pendingAmount += pendingAmount;
            current.count += 1;
            current.expenses.push(expense);
            map.set(key, current);
        });

        return Array.from(map.values()).sort((a, b) => b.pendingAmount - a.pendingAmount);
    }, [debtExpenses]);

    const filteredReimbursementDebtBreakdown = useMemo(() => {
        const value = debtPersonFilter.trim().toLowerCase();

        if (!value) {
            return reimbursementDebtBreakdown;
        }

        return reimbursementDebtBreakdown.filter(item => item.userName.toLowerCase().includes(value));
    }, [debtPersonFilter, reimbursementDebtBreakdown]);

    const loadCategories = async () => {
        const response = await getExpenseCategories(false);
        setCategories(response || []);
    };

    const loadExpenseOptions = async (organizationId?: string) => {
        const response = await getExpenseOptions(organizationId);
        setExpenseOptions(response || { providers: [], paymentMethods: [] });
    };

    const loadCompanyUsers = async (organizationId?: string) => {
        const response = await getCompanyUsers(organizationId || DEFAULT_ORGANIZATION_ID);
        setCompanyUsers(response || []);
    };

    const loadData = async () => {
        const params = {
            from: filters.from,
            to: filters.to,
            categoryId: filters.categoryId,
            organizationId: filters.organizationId,
            status: filters.status === 'TODOS' ? undefined : filters.status
        };

        const [expenseResponse, metricResponse] = await Promise.all([
            getExpenses(params),
            getFinancialMetrics({
                from: params.from,
                to: params.to,
                groupBy: filters.groupBy,
                organizationId: filters.organizationId
            })
        ]);

        setExpenses(expenseResponse || []);
        setMetrics(metricResponse || null);
        await loadExpenseOptions(filters.organizationId);
        await loadCompanyUsers(filters.organizationId);
    };

    useEffect(() => {
        loadCategories();
        loadExpenseOptions();
        loadCompanyUsers();
        searchOrganizations(DEFAULT_ORGANIZATION_NAME, 5).then(response => setOrganizations(response || []));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        loadData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters.groupBy]);

    const openNewExpense = () => {
        setSelectedExpense(null);
        setExpenseDocumentFile(null);
        expenseForm.resetFields();
        expenseForm.setFieldsValue({
            organizationId: DEFAULT_ORGANIZATION_ID,
            status: 'PENDIENTE',
            currency: 'USD',
            paymentSource: 'EMPRESA',
            iva: 0,
            subtotal: 0,
            expenseDate: getOrganizationToday(),
            reimbursementPaidAmount: 0
        });
        setExpenseModalOpen(true);
    };

    const openEditExpense = (expense: Expense) => {
        setSelectedExpense(expense);
        setExpenseDocumentFile(null);
        expenseForm.setFieldsValue({
            ...expense,
            expenseDate: toDateInput(expense.expenseDate),
            paymentDate: toDateInput(expense.paymentDate),
            paymentSource: expense.paymentSource || 'EMPRESA',
            reimbursementUserId: expense.reimbursement?.userId,
            reimbursementAmount: expense.reimbursement?.amount || expense.total || 0,
            reimbursementPaidAmount: expense.reimbursement?.paidAmount || 0,
            reimbursementPaymentDate: toDateInput(expense.reimbursement?.paymentDate),
            reimbursementNotes: expense.reimbursement?.notes
        });
        setExpenseModalOpen(true);
    };

    const handleExpenseDocumentChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];

        if (!file) {
            return;
        }

        setExpenseDocumentLoading(true);
        try {
            const value = await fileToBase64(file);
            setExpenseDocumentFile(value);
            message.success('Documento de respaldo listo para guardar.');
        } catch {
            setExpenseDocumentFile(null);
            message.error('No se pudo preparar el documento de respaldo.');
        } finally {
            setExpenseDocumentLoading(false);
        }
    };

    const openExpenseDocumentPreview = async () => {
        if (!selectedExpense?.id) {
            message.warning('No se encontro el gasto para visualizar el respaldo.');
            return;
        }

        const fileInfo = await getExpenseDocument(selectedExpense.id);
        if (!fileInfo?.sasUrl) {
            message.warning('No se pudo obtener el documento de respaldo.');
            return;
        }

        setDocumentPreview({ title: 'Documento de respaldo', url: fileInfo.sasUrl });
    };

    const renderExpenseDocumentPreview = () => {
        if (!selectedExpense?.id || !selectedExpense?.documentUrl) {
            return null;
        }

        return (
            <Button type="link" style={{ padding: 0 }} onClick={openExpenseDocumentPreview}>
                Ver documento guardado
            </Button>
        );
    };

    const submitExpense = async () => {
        let values;

        try {
            values = await expenseForm.validateFields();
        } catch {
            return;
        }

        const subtotal = Number(values.subtotal || 0);
        const iva = Number(values.iva || 0);
        const paymentSource = values.paymentSource || 'EMPRESA';
        const total = subtotal + iva;

        if (expenseDocumentLoading) {
            message.warning('Espere a que el archivo termine de cargarse.');
            return;
        }

        const payload = {
            ...values,
            id: selectedExpense?.id,
            expenseDate: toIsoDate(values.expenseDate),
            paymentDate: values.status === 'PAGADO' ? toIsoDate(values.paymentDate) : null,
            subtotal,
            iva,
            documentUrl: expenseDocumentFile ? null : (selectedExpense?.documentUrl || values.documentUrl || null),
            documentFile: expenseDocumentFile,
            paymentSource,
            reimbursement: paymentSource === 'EMPRESA' ? null : {
                id: selectedExpense?.reimbursement?.id || null,
                userId: values.reimbursementUserId,
                amount: Number(values.reimbursementAmount || total),
                paidAmount: Number(values.reimbursementPaidAmount || 0),
                paymentDate: values.reimbursementPaymentDate ? toIsoDate(values.reimbursementPaymentDate) : null,
                notes: values.reimbursementNotes || null
            }
        };

        if (values.status === 'PAGADO' && !values.paymentMethod) {
            message.warning('Seleccione el metodo de pago del gasto.');
            return;
        }

        if (paymentSource !== 'EMPRESA' && !values.reimbursementUserId) {
            message.warning('Seleccione la persona a la que se debe reembolsar.');
            return;
        }

        if (selectedExpense?.id) {
            await putExpense(payload);
        } else {
            await postExpense(payload);
        }

        setExpenseModalOpen(false);
        setExpenseDocumentFile(null);
        await loadExpenseOptions(values.organizationId);
        await loadData();
    };

    const submitCategory = async () => {
        let values;

        try {
            values = await categoryForm.validateFields();
        } catch {
            return;
        }

        await postExpenseCategory(values);
        setCategoryModalOpen(false);
        categoryForm.resetFields();
        await loadCategories();
    };

    const handleCancelExpense = (expense: Expense) => {
        Modal.confirm({
            title: 'Anular gasto',
            content: `Se anulara el gasto de ${expense.providerName}. Esta accion lo excluye de las metricas pagadas.`,
            okText: 'Anular',
            cancelText: 'Volver',
            okButtonProps: { danger: true },
            onOk: async () => {
                if (expense.id) {
                    await cancelExpense(expense.id);
                    await loadData();
                }
            }
        });
    };

    const openDebtBreakdown = async () => {
        setDebtModalOpen(true);
        setDebtPersonFilter('');
        setDebtLoading(true);

        const response = await getExpenses({
            from: filters.from,
            to: filters.to,
            categoryId: filters.categoryId,
            organizationId: filters.organizationId
        });

        setDebtExpenses(response || []);
        setDebtLoading(false);
    };

    const refreshDebtBreakdown = async () => {
        const response = await getExpenses({
            from: filters.from,
            to: filters.to,
            categoryId: filters.categoryId,
            organizationId: filters.organizationId
        });

        setDebtExpenses(response || []);
    };

    const openReimbursementEditor = (expense: Expense) => {
        setSelectedReimbursementExpense(expense);
        reimbursementForm.setFieldsValue({
            paidAmount: expense.reimbursement?.paidAmount || 0,
            paymentDate: toDateInput(expense.reimbursement?.paymentDate),
            notes: expense.reimbursement?.notes || ''
        });
        setReimbursementModalOpen(true);
    };

    const submitReimbursementPayment = async () => {
        const expense = selectedReimbursementExpense;

        if (!expense?.reimbursement) {
            message.warning('No se encontro informacion de reembolso para este gasto.');
            return;
        }

        let values;

        try {
            values = await reimbursementForm.validateFields();
        } catch {
            return;
        }

        const amount = Number(expense.reimbursement.amount || expense.total || 0);
        const paidAmount = Number(values.paidAmount || 0);

        if (paidAmount > amount) {
            message.warning('El monto reembolsado no puede superar el monto a reembolsar.');
            return;
        }

        const payload = {
            ...expense,
            expenseDate: expense.expenseDate,
            paymentDate: expense.paymentDate || null,
            reimbursement: {
                id: expense.reimbursement.id || null,
                userId: expense.reimbursement.userId,
                amount,
                paidAmount,
                paymentDate: values.paymentDate ? toIsoDate(values.paymentDate) : null,
                notes: values.notes || null
            }
        };

        await putExpense(payload);
        setReimbursementModalOpen(false);
        setSelectedReimbursementExpense(null);
        await refreshDebtBreakdown();
        await loadData();
    };

    const updateCategoryStatus = async (category: ExpenseCategory, status: 'ACTIVO' | 'INACTIVO') => {
        await putExpenseCategory({ ...category, status });
        await loadCategories();
    };

    const profitColor = (metrics?.profit || 0) < 0 ? '#ef4444' : '#19b37b';
    const margin = metrics?.profitMargin || 0;

    const columns = [
        {
            title: 'Gasto',
            key: 'expense',
            render: (_: unknown, row: Expense) => (
                <Space direction="vertical" size={2}>
                    <Text strong>{row.providerName}</Text>
                    <Text type="secondary">{row.description}</Text>
                    {row.number && <Text type="secondary">No. {row.number}</Text>}
                </Space>
            )
        },
        {
            title: 'Categoria',
            key: 'category',
            render: (_: unknown, row: Expense) => (
                <Space direction="vertical" size={2}>
                    <Text>{row.expenseCategoryName || 'Sin categoria'}</Text>
                    <Text type="secondary">{row.organizationName || row.organizationId}</Text>
                    {row.userName && <Text type="secondary">Persona: {row.userName}</Text>}
                </Space>
            )
        },
        {
            title: 'Fuente / reembolso',
            key: 'reimbursement',
            render: (_: unknown, row: Expense) => (
                <Space direction="vertical" size={2}>
                    <Tag color={row.paymentSource === 'EMPRESA' ? 'green' : 'blue'}>{row.paymentSource || 'EMPRESA'}</Tag>
                    {row.reimbursement ? (
                        <>
                            <Text>{row.reimbursement.userName}</Text>
                            <Text type="secondary">
                                Pendiente: {money(row.reimbursement.pendingAmount || 0, row.currency)}
                            </Text>
                            <Tag color={getStatusColor(row.reimbursement.status)}>{row.reimbursement.status}</Tag>
                        </>
                    ) : (
                        <Text type="secondary">Sin reembolso</Text>
                    )}
                </Space>
            )
        },
        {
            title: 'Fecha',
            key: 'date',
            render: (_: unknown, row: Expense) => (
                <Space direction="vertical" size={2}>
                    <Text>{formatDate(row.expenseDate)}</Text>
                    {row.paymentDate && <Text type="secondary">Pago: {formatDate(row.paymentDate)}</Text>}
                </Space>
            )
        },
        {
            title: 'Estado',
            dataIndex: 'status',
            key: 'status',
            render: (value: string) => <Tag color={getStatusColor(value)}>{value}</Tag>
        },
        {
            title: 'Total',
            dataIndex: 'total',
            key: 'total',
            align: 'right' as const,
            render: (value: number, row: Expense) => money(value, row.currency)
        },
        {
            title: '',
            key: 'actions',
            align: 'right' as const,
            render: (_: unknown, row: Expense) => (
                <Space>
                    <Button icon={<EditOutlined />} onClick={() => openEditExpense(row)}>
                        Editar
                    </Button>
                    {row.status !== 'ANULADO' && (
                        <Button danger icon={<StopOutlined />} onClick={() => handleCancelExpense(row)}>
                            Anular
                        </Button>
                    )}
                </Space>
            )
        }
    ];

    return (
        <Space direction="vertical" size={16} style={{ width: '100%' }}>
            <Card>
                <Row gutter={[12, 12]} align="bottom">
                    <Col xs={24} md={4}>
                        <Text strong>Desde</Text>
                        <Input
                            type="date"
                            value={filters.from}
                            onChange={(event) => setFilters(current => ({ ...current, from: event.target.value || undefined }))}
                            style={{ marginTop: 6 }}
                        />
                    </Col>
                    <Col xs={24} md={4}>
                        <Text strong>Hasta</Text>
                        <Input
                            type="date"
                            value={filters.to}
                            onChange={(event) => setFilters(current => ({ ...current, to: event.target.value || undefined }))}
                            style={{ marginTop: 6 }}
                        />
                    </Col>
                    <Col xs={24} md={4}>
                        <Text strong>Estado</Text>
                        <Select
                            value={filters.status}
                            onChange={(value) => setFilters(current => ({ ...current, status: value }))}
                            options={[
                                { value: 'TODOS', label: 'Todos' },
                                { value: 'PENDIENTE', label: 'Pendiente' },
                                { value: 'PAGADO', label: 'Pagado' },
                                { value: 'ANULADO', label: 'Anulado' }
                            ]}
                            style={{ width: '100%', marginTop: 6 }}
                        />
                    </Col>
                    <Col xs={24} md={5}>
                        <Text strong>Categoria</Text>
                        <Select
                            allowClear
                            value={filters.categoryId}
                            onChange={(value) => setFilters(current => ({ ...current, categoryId: value }))}
                            options={categoryOptions}
                            placeholder="Todas"
                            style={{ width: '100%', marginTop: 6 }}
                        />
                    </Col>
                    <Col xs={24} md={5}>
                        <Text strong>Empresa</Text>
                        <Select
                            showSearch
                            allowClear
                            value={filters.organizationId}
                            onSearch={(value) => searchOrganizations(value, 10).then(response => setOrganizations(response || []))}
                            onChange={(value) => setFilters(current => ({ ...current, organizationId: value }))}
                            filterOption={false}
                            options={organizationOptions}
                            placeholder="Todas"
                            style={{ width: '100%', marginTop: 6 }}
                        />
                    </Col>
                    <Col xs={24} md={2}>
                        <Button block type="primary" icon={<ReloadOutlined />} onClick={loadData}>
                            Buscar
                        </Button>
                    </Col>
                    <Col xs={24} md={24}>
                        <Space wrap>
                            <Button type="primary" icon={<PlusOutlined />} onClick={openNewExpense}>
                                Nuevo gasto
                            </Button>
                            <Button icon={<PlusOutlined />} onClick={() => setCategoryModalOpen(true)}>
                                Categoria
                            </Button>
                            <Button
                                onClick={() => {
                                    setFilters({ status: 'TODOS', groupBy: 'month' });
                                    setTimeout(loadData, 0);
                                }}
                            >
                                Limpiar
                            </Button>
                            <Select
                                value={filters.groupBy}
                                onChange={(value) => setFilters(current => ({ ...current, groupBy: value }))}
                                options={[
                                    { value: 'day', label: 'Dias' },
                                    { value: 'month', label: 'Meses' },
                                    { value: 'quarter', label: 'Trimestres' },
                                    { value: 'year', label: 'Anios' }
                                ]}
                                style={{ width: 150 }}
                            />
                        </Space>
                    </Col>
                </Row>
            </Card>

            <Row gutter={[12, 12]}>
                <Col xs={24} md={4}>
                    <Card>
                        <Statistic title="Ingresos" value={metrics?.income || 0} precision={2} prefix="$" />
                    </Card>
                </Col>
                <Col xs={24} md={4}>
                    <Card>
                        <Statistic title="Gastos" value={metrics?.expenses || 0} precision={2} prefix="$" valueStyle={{ color: '#ef4444' }} />
                    </Card>
                </Col>
                <Col xs={24} md={4}>
                    <Card>
                        <Statistic title="Ganancia" value={metrics?.profit || 0} precision={2} prefix="$" valueStyle={{ color: profitColor }} />
                    </Card>
                </Col>
                <Col xs={24} md={4}>
                    <Card>
                        <Statistic title="Margen" value={margin} precision={2} suffix="%" valueStyle={{ color: profitColor }} />
                    </Card>
                </Col>
                <Col xs={24} md={4}>
                    <Card>
                        <Statistic title="Gastos pendientes" value={metrics?.pendingExpenses || 0} precision={2} prefix="$" valueStyle={{ color: '#f5a623' }} />
                    </Card>
                </Col>
                <Col xs={24} md={4}>
                    <Card>
                        <Space direction="vertical" size={8} style={{ width: '100%' }}>
                            <Statistic title="Deuda a personas" value={metrics?.reimbursementDebt || 0} precision={2} prefix="$" valueStyle={{ color: '#ef4444' }} />
                            <Button size="small" icon={<EyeOutlined />} onClick={openDebtBreakdown} disabled={!metrics?.reimbursementDebt}>
                                Ver detalle
                            </Button>
                        </Space>
                    </Card>
                </Col>
            </Row>

            <Row gutter={[12, 12]}>
                <Col xs={24} xl={15}>
                    <Card title="Ingresos vs gastos">
                        {metrics?.series?.length ? (
                            <ResponsiveContainer width="100%" height={320}>
                                <BarChart data={metrics.series.map(item => ({ ...item, label: shortLabel(item.period, 12) }))}>
                                    <CartesianGrid strokeDasharray="4 5" vertical={false} />
                                    <XAxis dataKey="label" tickLine={false} axisLine={false} />
                                    <YAxis tickFormatter={(value) => money(Number(value)).replace(',00', '')} tickLine={false} axisLine={false} width={72} />
                                    <Tooltip formatter={(value, name) => [money(Number(value || 0)), name === 'income' ? 'Ingresos' : name === 'expenses' ? 'Gastos' : 'Ganancia']} />
                                    <Legend />
                                    <Bar dataKey="income" name="Ingresos" fill="#2177f5" radius={[8, 8, 0, 0]} />
                                    <Bar dataKey="expenses" name="Gastos" fill="#ef4444" radius={[8, 8, 0, 0]} />
                                    <Bar dataKey="profit" name="Ganancia" fill="#19b37b" radius={[8, 8, 0, 0]} />
                                </BarChart>
                            </ResponsiveContainer>
                        ) : <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No hay datos para graficar." />}
                    </Card>
                </Col>
                <Col xs={24} xl={9}>
                    <Card title="Gastos por categoria">
                        {metrics?.expensesByCategory?.length ? (
                            <ResponsiveContainer width="100%" height={320}>
                                <PieChart>
                                    <Pie
                                        data={metrics.expensesByCategory}
                                        dataKey="total"
                                        nameKey="categoryName"
                                        cx="50%"
                                        cy="50%"
                                        innerRadius={62}
                                        outerRadius={96}
                                        paddingAngle={3}
                                    >
                                        {metrics.expensesByCategory.map((row, index) => (
                                            <Cell key={row.categoryId || row.categoryName} fill={chartColors[index % chartColors.length]} />
                                        ))}
                                    </Pie>
                                    <Tooltip formatter={(value) => [money(Number(value || 0)), 'Gasto']} />
                                    <Legend formatter={(value) => shortLabel(String(value), 18)} />
                                </PieChart>
                            </ResponsiveContainer>
                        ) : <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No hay gastos por categoria." />}
                    </Card>
                </Col>
            </Row>

            <Card title="Registro de gastos">
                <Space direction="vertical" size={12} style={{ width: '100%' }}>
                    <Row gutter={[12, 12]} align="bottom">
                        <Col xs={24} md={7}>
                            <Text strong>Buscar</Text>
                            <Input
                                allowClear
                                value={filters.search}
                                placeholder="Proveedor, descripcion, numero, empresa..."
                                onChange={(event) => setFilters(current => ({ ...current, search: event.target.value || undefined }))}
                                style={{ marginTop: 6 }}
                            />
                        </Col>
                        <Col xs={24} md={5}>
                            <Text strong>Proveedor</Text>
                            <AutoComplete
                                allowClear
                                value={filters.providerName}
                                options={providerOptions}
                                placeholder="Todos"
                                onChange={(value) => setFilters(current => ({ ...current, providerName: value || undefined }))}
                                filterOption={(inputValue, option) => String(option?.value || '').toLowerCase().includes(inputValue.toLowerCase())}
                                style={{ width: '100%', marginTop: 6 }}
                            />
                        </Col>
                        <Col xs={24} md={4}>
                            <Text strong>Fuente</Text>
                            <Select
                                allowClear
                                value={filters.paymentSource}
                                placeholder="Todas"
                                onChange={(value) => setFilters(current => ({ ...current, paymentSource: value }))}
                                options={[
                                    { value: 'EMPRESA', label: 'Empresa' },
                                    { value: 'SOCIO', label: 'Socio' },
                                    { value: 'TRABAJADOR', label: 'Trabajador' },
                                    { value: 'TERCERO', label: 'Tercero' }
                                ]}
                                style={{ width: '100%', marginTop: 6 }}
                            />
                        </Col>
                        <Col xs={24} md={5}>
                            <Text strong>Persona</Text>
                            <Select
                                allowClear
                                showSearch
                                value={filters.userId}
                                optionFilterProp="label"
                                placeholder="Todas"
                                onChange={(value) => setFilters(current => ({ ...current, userId: value }))}
                                options={companyUserOptions}
                                style={{ width: '100%', marginTop: 6 }}
                            />
                        </Col>
                        <Col xs={24} md={3}>
                            <Button
                                block
                                onClick={() => setFilters(current => ({
                                    ...current,
                                    search: undefined,
                                    providerName: undefined,
                                    paymentSource: undefined,
                                    userId: undefined
                                }))}
                            >
                                Limpiar tabla
                            </Button>
                        </Col>
                    </Row>
                    <Table
                        rowKey={(row) => row.id || `${row.providerName}-${row.expenseDate}`}
                        columns={columns}
                        dataSource={filteredExpenses}
                        pagination={{ pageSize: 8 }}
                        scroll={{ x: 980 }}
                    />
                </Space>
            </Card>

            <Modal
                title={selectedExpense ? 'Editar gasto' : 'Nuevo gasto'}
                open={expenseModalOpen}
                onCancel={() => setExpenseModalOpen(false)}
                onOk={submitExpense}
                okText="Guardar"
                cancelText="Cancelar"
                width={980}
                destroyOnHidden
            >
                <Form form={expenseForm} layout="vertical">
                    <Row gutter={12}>
                        <Col xs={24} md={12}>
                            <Form.Item name="organizationId" label="Empresa" rules={[{ required: true, message: 'Seleccione la empresa' }]}>
                                <Select
                                    showSearch
                                    filterOption={false}
                                    onSearch={(value) => searchOrganizations(value, 10).then(response => setOrganizations(response || []))}
                                    options={organizationOptions}
                                />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={12}>
                            <Form.Item name="expenseCategoryId" label="Categoria" rules={[{ required: true, message: 'Seleccione la categoria' }]}>
                                <Select options={categoryOptions} />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={12}>
                            <Form.Item name="userId" label="Persona relacionada">
                                <Select
                                    allowClear
                                    showSearch
                                    optionFilterProp="label"
                                    options={companyUserOptions}
                                    placeholder="Solo si aplica"
                                />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={12}>
                            <Form.Item name="paymentSource" label="Fuente de pago" rules={[{ required: true, message: 'Seleccione la fuente de pago' }]}>
                                <Select
                                    options={[
                                        { value: 'EMPRESA', label: 'Empresa' },
                                        { value: 'SOCIO', label: 'Socio' },
                                        { value: 'TRABAJADOR', label: 'Trabajador' },
                                        { value: 'TERCERO', label: 'Tercero' }
                                    ]}
                                />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={8}>
                            <Form.Item name="number" label="Numero">
                                <Input placeholder="Factura, recibo o referencia" />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={8}>
                            <Form.Item name="providerName" label="Proveedor" rules={[{ required: true, message: 'Ingrese el proveedor' }]}>
                                <AutoComplete
                                    options={providerOptions}
                                    filterOption={(inputValue, option) =>
                                        String(option?.value || '').toUpperCase().includes(inputValue.toUpperCase())
                                    }
                                    placeholder="Seleccione o escriba un proveedor"
                                />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={8}>
                            <Form.Item name="expenseDate" label="Fecha del gasto" rules={[{ required: true, message: 'Ingrese la fecha' }]}>
                                <Input type="date" />
                            </Form.Item>
                        </Col>
                        <Col xs={24}>
                            <Form.Item name="description" label="Descripcion" rules={[{ required: true, message: 'Ingrese la descripcion' }]}>
                                <Input.TextArea rows={2} />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={6}>
                            <Form.Item name="status" label="Estado" rules={[{ required: true, message: 'Seleccione el estado' }]}>
                                <Select
                                    options={[
                                        { value: 'PENDIENTE', label: 'Pendiente' },
                                        { value: 'PAGADO', label: 'Pagado' },
                                        { value: 'ANULADO', label: 'Anulado' }
                                    ]}
                                />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={6}>
                            <Form.Item name="paymentDate" label="Fecha de pago">
                                <Input type="date" />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={6}>
                            <Form.Item name="paymentMethod" label="Metodo de pago">
                                <AutoComplete
                                    options={paymentMethodOptions}
                                    filterOption={(inputValue, option) =>
                                        String(option?.value || '').toUpperCase().includes(inputValue.toUpperCase())
                                    }
                                    placeholder="Seleccione o escriba un metodo"
                                />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={6}>
                            <Form.Item name="currency" label="Moneda" rules={[{ required: true, message: 'Ingrese la moneda' }]}>
                                <Input maxLength={6} />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={8}>
                            <Form.Item name="subtotal" label="Subtotal" rules={[{ required: true, message: 'Ingrese el subtotal' }]}>
                                <InputNumber min={0} precision={2} style={{ width: '100%' }} />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={8}>
                            <Form.Item name="iva" label="IVA" rules={[{ required: true, message: 'Ingrese el IVA' }]}>
                                <InputNumber min={0} precision={2} style={{ width: '100%' }} />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={8}>
                            <Form.Item shouldUpdate noStyle>
                                {() => (
                                    <Form.Item label="Total">
                                        <Input
                                            prefix={<DollarOutlined />}
                                            value={money(Number(expenseForm.getFieldValue('subtotal') || 0) + Number(expenseForm.getFieldValue('iva') || 0))}
                                            readOnly
                                        />
                                    </Form.Item>
                                )}
                            </Form.Item>
                        </Col>
                        <Col xs={24}>
                            <Form.Item label="Documento de respaldo">
                                <Space direction="vertical" size={4} style={{ width: '100%' }}>
                                    <Input type="file" onChange={handleExpenseDocumentChange} />
                                    {expenseDocumentFile && <Text type="secondary">{expenseDocumentFile.fileName}</Text>}
                                    {!expenseDocumentFile && selectedExpense?.documentUrl && (
                                        <Space direction="vertical" size={2}>
                                            {renderExpenseDocumentPreview()}
                                            <Text type="secondary">Seleccione otro archivo solo si desea reemplazarlo.</Text>
                                        </Space>
                                    )}
                                </Space>
                            </Form.Item>
                        </Col>
                        <Form.Item shouldUpdate noStyle>
                            {() => expenseForm.getFieldValue('paymentSource') !== 'EMPRESA' && (
                                <>
                                    <Col xs={24}>
                                        <Alert
                                            type="warning"
                                            showIcon
                                            message="Este gasto genera una deuda de reembolso para la empresa."
                                            style={{ marginBottom: 12 }}
                                        />
                                    </Col>
                                    <Col xs={24} md={8}>
                                        <Form.Item
                                            name="reimbursementUserId"
                                            label="Persona a reembolsar"
                                            rules={[{ required: true, message: 'Seleccione la persona' }]}
                                        >
                                            <Select
                                                showSearch
                                                optionFilterProp="label"
                                                options={companyUserOptions}
                                            />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} md={8}>
                                        <Form.Item name="reimbursementAmount" label="Monto a reembolsar">
                                            <InputNumber min={0} precision={2} style={{ width: '100%' }} />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} md={8}>
                                        <Form.Item name="reimbursementPaidAmount" label="Monto reembolsado">
                                            <InputNumber min={0} precision={2} style={{ width: '100%' }} />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} md={8}>
                                        <Form.Item name="reimbursementPaymentDate" label="Fecha de reembolso">
                                            <Input type="date" />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} md={16}>
                                        <Form.Item name="reimbursementNotes" label="Notas de reembolso">
                                            <Input placeholder="Ej. Pablo cubrio Azure con tarjeta personal" />
                                        </Form.Item>
                                    </Col>
                                </>
                            )}
                        </Form.Item>
                        <Col xs={24}>
                            <Form.Item name="notes" label="Notas">
                                <Input.TextArea rows={2} />
                            </Form.Item>
                        </Col>
                    </Row>
                </Form>
            </Modal>

            <Modal
                title="Detalle de deuda a personas"
                open={debtModalOpen}
                onCancel={() => setDebtModalOpen(false)}
                footer={[
                    <Button key="close" onClick={() => setDebtModalOpen(false)}>Cerrar</Button>
                ]}
                width={980}
                destroyOnHidden
            >
                <Space direction="vertical" size={16} style={{ width: '100%' }}>
                    <Alert
                        showIcon
                        type="info"
                        message={`Total pendiente: ${money(filteredReimbursementDebtBreakdown.reduce((sum, item) => sum + item.pendingAmount, 0))}`}
                        description="Este detalle agrupa los gastos pagados por socios, trabajadores o terceros que todavia tienen saldo por reembolsar."
                    />
                    <Input
                        allowClear
                        placeholder="Buscar por persona"
                        value={debtPersonFilter}
                        onChange={(event) => setDebtPersonFilter(event.target.value)}
                    />
                    <Table
                        rowKey="key"
                        loading={debtLoading}
                        dataSource={filteredReimbursementDebtBreakdown}
                        pagination={false}
                        columns={[
                            {
                                title: 'Persona',
                                dataIndex: 'userName',
                                render: (value: string, row: ReimbursementDebtGroup) => (
                                    <Space direction="vertical" size={0}>
                                        <Text strong>{value}</Text>
                                        <Text type="secondary">{row.count} gasto{row.count === 1 ? '' : 's'} pendiente{row.count === 1 ? '' : 's'}</Text>
                                    </Space>
                                )
                            },
                            {
                                title: 'Monto cubierto',
                                dataIndex: 'amount',
                                align: 'right' as const,
                                render: (value: number, row: ReimbursementDebtGroup) => money(value, row.currency)
                            },
                            {
                                title: 'Reembolsado',
                                dataIndex: 'paidAmount',
                                align: 'right' as const,
                                render: (value: number, row: ReimbursementDebtGroup) => money(value, row.currency)
                            },
                            {
                                title: 'Pendiente',
                                dataIndex: 'pendingAmount',
                                align: 'right' as const,
                                render: (value: number, row: ReimbursementDebtGroup) => <Text type="danger" strong>{money(value, row.currency)}</Text>
                            }
                        ]}
                        expandable={{
                            expandedRowRender: (row: ReimbursementDebtGroup) => (
                                <Table
                                    size="small"
                                    rowKey={(expense) => expense.id || `${expense.providerName}-${expense.expenseDate}`}
                                    dataSource={row.expenses}
                                    pagination={false}
                                    columns={[
                                        {
                                            title: 'Gasto',
                                            render: (_, expense: Expense) => (
                                                <Space direction="vertical" size={0}>
                                                    <Text strong>{expense.providerName}</Text>
                                                    <Text type="secondary">{expense.description}</Text>
                                                </Space>
                                            )
                                        },
                                        {
                                            title: 'Fecha',
                                            dataIndex: 'expenseDate',
                                            render: (value: string) => formatDate(value)
                                        },
                                        {
                                            title: 'Fuente',
                                            dataIndex: 'paymentSource',
                                            render: (value: string) => <Tag color="blue">{value || 'PERSONA'}</Tag>
                                        },
                                        {
                                            title: 'Estado',
                                            render: (_, expense: Expense) => <Tag color={getStatusColor(expense.reimbursement?.status)}>{expense.reimbursement?.status}</Tag>
                                        },
                                        {
                                            title: 'Pendiente',
                                            align: 'right' as const,
                                            render: (_, expense: Expense) => money(expense.reimbursement?.pendingAmount || 0, expense.currency)
                                        },
                                        {
                                            title: 'Accion',
                                            align: 'right' as const,
                                            render: (_, expense: Expense) => (
                                                <Button size="small" icon={<DollarOutlined />} onClick={() => openReimbursementEditor(expense)}>
                                                    Reembolsar
                                                </Button>
                                            )
                                        }
                                    ]}
                                />
                            )
                        }}
                    />
                </Space>
            </Modal>

            <Modal
                title="Registrar reembolso"
                open={reimbursementModalOpen}
                onCancel={() => setReimbursementModalOpen(false)}
                onOk={submitReimbursementPayment}
                okText="Guardar reembolso"
                cancelText="Cancelar"
                destroyOnHidden
            >
                <Space direction="vertical" size={12} style={{ width: '100%' }}>
                    {selectedReimbursementExpense?.reimbursement && (
                        <Alert
                            showIcon
                            type="info"
                            message={selectedReimbursementExpense.providerName}
                            description={`Pendiente actual: ${money(selectedReimbursementExpense.reimbursement.pendingAmount || 0, selectedReimbursementExpense.currency)} de ${money(selectedReimbursementExpense.reimbursement.amount || 0, selectedReimbursementExpense.currency)}`}
                        />
                    )}
                    <Form form={reimbursementForm} layout="vertical">
                        <Form.Item
                            name="paidAmount"
                            label="Monto reembolsado acumulado"
                            rules={[{ required: true, message: 'Ingrese el monto reembolsado' }]}
                        >
                            <InputNumber min={0} precision={2} style={{ width: '100%' }} />
                        </Form.Item>
                        <Form.Item name="paymentDate" label="Fecha de reembolso">
                            <Input type="date" />
                        </Form.Item>
                        <Form.Item name="notes" label="Notas">
                            <Input.TextArea rows={3} placeholder="Ej. Reembolso parcial por transferencia" />
                        </Form.Item>
                    </Form>
                </Space>
            </Modal>

            <Modal
                title="Nueva categoria de gasto"
                open={categoryModalOpen}
                onCancel={() => setCategoryModalOpen(false)}
                onOk={submitCategory}
                okText="Guardar"
                cancelText="Cancelar"
                destroyOnHidden
            >
                <Form form={categoryForm} layout="vertical" initialValues={{ status: 'ACTIVO' }}>
                    <Form.Item name="name" label="Nombre" rules={[{ required: true, message: 'Ingrese el nombre' }]}>
                        <Input placeholder="SERVIDORES, DOMINIOS, NOMINA..." />
                    </Form.Item>
                    <Form.Item name="description" label="Descripcion">
                        <Input.TextArea rows={2} />
                    </Form.Item>
                    <Form.Item name="status" label="Estado" rules={[{ required: true, message: 'Seleccione el estado' }]}>
                        <Select
                            options={[
                                { value: 'ACTIVO', label: 'Activo' },
                                { value: 'INACTIVO', label: 'Inactivo' }
                            ]}
                        />
                    </Form.Item>
                </Form>
                <Space direction="vertical" style={{ width: '100%' }}>
                    {categories.map(category => (
                        <Card size="small" key={category.id || category.name}>
                            <Row align="middle" gutter={8}>
                                <Col flex="auto">
                                    <Text strong>{category.name}</Text>
                                    <br />
                                    <Text type="secondary">{category.description || 'Sin descripcion'}</Text>
                                </Col>
                                <Col>
                                    <Tag color={category.status === 'ACTIVO' ? 'green' : 'red'}>{category.status}</Tag>
                                </Col>
                                <Col>
                                    <Button
                                        size="small"
                                        onClick={() => updateCategoryStatus(category, category.status === 'ACTIVO' ? 'INACTIVO' : 'ACTIVO')}
                                    >
                                        {category.status === 'ACTIVO' ? 'Inactivar' : 'Activar'}
                                    </Button>
                                </Col>
                            </Row>
                        </Card>
                    ))}
                </Space>
            </Modal>

            <Modal
                title={documentPreview?.title || 'Documento'}
                open={!!documentPreview}
                onCancel={() => setDocumentPreview(null)}
                footer={[
                    <Button key="close" onClick={() => setDocumentPreview(null)}>Cerrar</Button>
                ]}
                width="90vw"
                style={{ top: 24 }}
                destroyOnHidden
                bodyStyle={{ padding: 0, background: '#111827' }}
            >
                {documentPreview?.url && (
                    <Space direction="vertical" size={0} style={{ width: '100%' }}>
                        <Alert
                            type="info"
                            showIcon
                            message="Visualizando el archivo guardado en Azure"
                            description="Si Azure muestra ResourceNotFound, el registro tiene una URL guardada pero el archivo no existe en el contenedor. Seleccione nuevamente el respaldo y guarde el gasto para reemplazarlo."
                        />
                        <iframe
                            title={documentPreview.title}
                            src={documentPreview.url}
                            style={{ width: '100%', height: '74vh', border: 0, background: '#111827' }}
                        />
                    </Space>
                )}
            </Modal>
        </Space>
    );
};

export default PlanBillingExpenses;
